/**
 * PotApprovalListItem Molecule
 * Displays a single pot row in the approval list
 * Pattern: Atomic Design - Molecules, follows SiteListItem / UserListItem styling
 */

import React, { useCallback } from 'react';
import {
  Avatar,
  Box,
  Chip,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ScheduleSend as ScheduleSendIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../shared/utils/currency';
import { PotActionsMenu } from './PotActionsMenu';
import type { PotSummary } from '../types/pot.types';

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export interface PotApprovalListItemProps {
  pot: PotSummary;
  onClick?: () => void;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  'data-testid'?: string;
}

/**
 * Format period dates for tooltip (e.g., "Nov 3 - Nov 9, 2025")
 */
function formatPeriodTooltip(start: string, end: string, year: number): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    day: 'numeric',
  });

  const startFormatted = formatter.format(startDate);
  const endFormatted = formatter.format(endDate);

  return `${startFormatted} - ${endFormatted}, ${year}`;
}

export const PotApprovalListItem = React.memo(function PotApprovalListItem({
  pot,
  onClick,
  onMoveToActivePeriod,
  onMoveToPrimary,
  'data-testid': dataTestId,
}: PotApprovalListItemProps) {
  const handleClick = useCallback(() => onClick?.(), [onClick]);
  const baseTestId = dataTestId ?? `pot-${pot.id}`;

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid={`${baseTestId}-row`}
      data-is-primary={pot.isPrimary}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(pot.owner.name),
          flexShrink: 0,
        }}
      >
        {getInitials(pot.owner.name)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {pot.owner.name}
        </Typography>
        <Tooltip
          title={formatPeriodTooltip(
            pot.period.start,
            pot.period.end,
            pot.period.year
          )}
          arrow
        >
          <Typography variant="caption" color="text.secondary">
            Week {pot.period.weekNumber}
          </Typography>
        </Tooltip>
      </Box>

      {pot.isScheduledForAutoSubmit && (
        <Tooltip title="Scheduled for automatic submission at the deadline">
          <Chip
            icon={<ScheduleSendIcon sx={{ fontSize: '0.6875rem' }} />}
            label="Auto"
            size="small"
            variant="outlined"
            color="default"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexShrink: 0,
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 500,
              '& .MuiChip-label': { px: '6px' },
              '& .MuiChip-icon': { ml: '4px' },
            }}
            data-testid="pot-auto-submit-indicator"
          />
        </Tooltip>
      )}

      {/* Amount — hidden on xs, shown on sm+ */}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: 80,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {formatCurrency(pot.netAmount)}
      </Typography>

      <PotActionsMenu
        potId={pot.id}
        status={pot.status}
        isPrimary={pot.isPrimary}
        onMoveToActivePeriod={onMoveToActivePeriod}
        onMoveToPrimary={onMoveToPrimary}
        data-testid={`${baseTestId}-actions`}
      />
    </ListItemButton>
  );
});
